import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tag, Copy, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface Coupon {
  id: string;
  title: string;
  description: string;
  discount_code: string;
  discount_percentage?: number;
  discount_amount?: number;
  valid_until: string;
  is_active: boolean;
}

interface ListingCouponsProps {
  coupons: Coupon[];
}

export const ListingCoupons = ({ coupons }: ListingCouponsProps) => {
  const activeCoupons = coupons.filter(
    c => c.is_active && new Date(c.valid_until) > new Date()
  );

  if (activeCoupons.length === 0) return null;

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success("Código copiado al portapapeles");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-primary" />
          Cupones y Ofertas
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activeCoupons.map((coupon) => (
            <div
              key={coupon.id}
              className="p-4 rounded-lg border-2 border-dashed border-primary/30 bg-gradient-to-r from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-colors"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-bold text-lg mb-1">{coupon.title}</h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {coupon.description}
                  </p>
                  <div className="flex items-center gap-2 mb-2">
                    {coupon.discount_percentage && (
                      <Badge className="bg-gradient-primary text-white text-lg px-3 py-1">
                        {coupon.discount_percentage}% DESCUENTO
                      </Badge>
                    )}
                    {coupon.discount_amount && (
                      <Badge className="bg-gradient-primary text-white text-lg px-3 py-1">
                        {coupon.discount_amount}€ DESCUENTO
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="w-3 h-3" />
                    <span>
                      Válido hasta{" "}
                      {format(new Date(coupon.valid_until), "d 'de' MMMM, yyyy", { locale: es })}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <div className="flex-1 p-2 bg-background rounded border font-mono text-center font-bold text-lg">
                  {coupon.discount_code}
                </div>
                <Button
                  size="sm"
                  onClick={() => copyCode(coupon.discount_code)}
                  className="flex items-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copiar
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
